'use client'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tables } from '@/supabase/types'
import { useNotes } from '@/hooks/useNotes'
import {
  filterEmptyChatWidgetsForNexusAccess,
  type EmptyChatWidgetsState,
  type EmptyChatWidgetId,
  setEmptyChatWidgetsState
} from '@/lib/chat-empty-widgets-state'
import { WidgetGrid } from './widget-grid'
import { CustomizeModal, WidgetSettingsDialog } from './customize-modal'
import { Button } from '@/components/ui/button'
import { IconSettings } from '@tabler/icons-react'

const WIDGET_IDS_WITH_SETTINGS: EmptyChatWidgetId[] = [
  'clock',
  'weather',
  'news',
  'finance',
  'sticky'
]

export { CustomizeModal } from './customize-modal'
export { WidgetGrid } from './widget-grid'

interface EmptyChatWidgetsSectionProps {
  widgetState: EmptyChatWidgetsState
  onWidgetStateChange: (state: EmptyChatWidgetsState) => void
  chats: Tables<'chats'>[]
  prompts: Tables<'prompts'>[]
  setUserInput: (value: string) => void
  onSendMessage?: (text: string) => void
  onSelectPrompt?: (prompt: Tables<'prompts'>) => void
  customizeOpen: boolean
  onCustomizeOpenChange: (open: boolean) => void
  hasNexusAccess?: boolean
}

export function EmptyChatWidgetsSection({
  widgetState,
  onWidgetStateChange,
  chats,
  prompts,
  setUserInput,
  onSendMessage,
  onSelectPrompt,
  customizeOpen,
  onCustomizeOpenChange,
  hasNexusAccess = false
}: EmptyChatWidgetsSectionProps) {
  const { t } = useTranslation()
  const { notes } = useNotes()
  const [openSettingsForInstanceKey, setOpenSettingsForInstanceKey] = useState<string | null>(null)
  const visibleWidgetState = React.useMemo(
    () => filterEmptyChatWidgetsForNexusAccess(widgetState, hasNexusAccess),
    [hasNexusAccess, widgetState]
  )

  const handleRemoveInstance = (index: number) => {
    const next: EmptyChatWidgetsState = {
      ...visibleWidgetState,
      enabledWidgets: visibleWidgetState.enabledWidgets.filter((_, i) => i !== index)
    }
    onWidgetStateChange(next)
    setEmptyChatWidgetsState(next)
  }

  return (
    <>
      <div className="mt-6 w-full">
        <div className="w-full space-y-6">
          <WidgetGrid
            enabledWidgets={visibleWidgetState.enabledWidgets}
            widgetSettingsMap={visibleWidgetState.widgetSettingsMap}
            widgetSettings={visibleWidgetState.widgetSettings}
            chats={chats}
            prompts={prompts}
            notes={notes}
            setUserInput={setUserInput}
            onSendMessage={onSendMessage}
            onSelectPrompt={onSelectPrompt}
            hasNexusAccess={hasNexusAccess}
            onStickyContentChange={(instanceKey, content) => {
              const map = {
                ...(visibleWidgetState.widgetSettingsMap ?? {}),
                [instanceKey]: {
                  ...(visibleWidgetState.widgetSettingsMap?.[instanceKey] ?? {}),
                  stickyContent: content
                }
              }
              const next: EmptyChatWidgetsState = {
                ...visibleWidgetState,
                widgetSettingsMap: map
              }
              onWidgetStateChange(next)
              setEmptyChatWidgetsState(next)
            }}
            onOpenSettings={setOpenSettingsForInstanceKey}
            onRemoveInstance={handleRemoveInstance}
          />
          <div className="flex justify-center pb-4">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full border border-border/60 bg-background/70 px-3 text-muted-foreground shadow-sm backdrop-blur-sm transition-[transform,color,background-color,border-color] hover:text-foreground active:scale-[0.98]"
              onClick={() => onCustomizeOpenChange(true)}
              aria-label={t('chat.empty_chat.customize', 'Customize')}
            >
              <IconSettings size={16} className="mr-1.5" />
              {t('chat.empty_chat.customize', 'Customize')}
            </Button>
          </div>
        </div>
      </div>
      <CustomizeModal
        open={customizeOpen}
        onOpenChange={onCustomizeOpenChange}
        state={visibleWidgetState}
        onStateChange={onWidgetStateChange}
        hasNexusAccess={hasNexusAccess}
      />

      {openSettingsForInstanceKey &&
        (() => {
          const widgetId = openSettingsForInstanceKey.split('_')[0] as EmptyChatWidgetId
          if (!WIDGET_IDS_WITH_SETTINGS.includes(widgetId)) return null
          const settings = visibleWidgetState.widgetSettingsMap?.[openSettingsForInstanceKey] ?? {}
          return (
            <WidgetSettingsDialog
              widgetId={widgetId}
              settings={settings}
              onSave={(next) => {
                const map = {
                  ...(visibleWidgetState.widgetSettingsMap ?? {}),
                  [openSettingsForInstanceKey]: next
                }
                const nextState: EmptyChatWidgetsState = {
                  ...visibleWidgetState,
                  widgetSettingsMap: map
                }
                onWidgetStateChange(nextState)
                setEmptyChatWidgetsState(nextState)
                setOpenSettingsForInstanceKey(null)
              }}
              open={true}
              onOpenChange={(o) => !o && setOpenSettingsForInstanceKey(null)}
            />
          )
        })()}
    </>
  )
}
